import { useTheme } from '@fluentui/react/lib/Theme';
import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPLabel,
  IPModal,
  IPPrimaryButton,
  IPSeparator,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ChecklistContainer, ChecklistMode } from 'components/Checklist/ChecklistContainer';
import StepIcon, { StepIconModes, stepIconSizeRatios, StepIconSizes } from 'components/Utils/IconsAndImages/StepIcon';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import OverlayLoader from 'components/Loading/OverlayLoader';
import TextInfo from 'components/Notification/Info';
import InlineSuggestions from 'components/Suggestions/InlineSuggestions';
import { getTaskViewModalStyles } from 'components/Tasks/View/TaskRenderHelpers';
import { globalKB_howToWorkWithFormsChecklist } from 'globalConstants';
import {
  cancelIcon,
  extLinkIcon,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalTextStylesBold,
  leftArrowIcon,
} from 'globalStyles';
import Joi from 'joi';
import { TaskCheckList } from 'models/tasks/taskHelperClasses';
import { Fragment, KeyboardEvent, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { addDateDays } from 'utils/datetime';
import { ISingleTaskTypeProps } from '../../SingleTask';
import { TaskDetailAssignment } from '../Details/TaskDetailAssignment';
import { TaskDetailDeadline } from '../Details/TaskDetailDeadline';
import { TaskDetailDescription } from '../Details/TaskDetailDecription';
import { TaskDetailName } from '../Details/TaskDetailName';
import { findTaskFieldError, getTaskValidationLabels } from '../taskValidationHelpers';
import { getEndOfMonth, setWizardDeadlineDay } from './newTaskWizardHelpers';
import {
  getWizardBodyStyles,
  getWizardRootStyles,
  getWizardSectionStyles,
  wizardChecklistMinHeight,
  wizardStepLabelWidth,
} from './newTaskWizardStyles';

export type WizardStep = 1 | 2;

const wizardStepCount = 2;

//presses on the primary button this soon after the step changed are ignored (a double click on
//Next must not also Create)
const stepChangeGuardMs = 400;

export interface INewTaskWizardProps
  extends Pick<
    ISingleTaskTypeProps,
    | 'isOpen'
    | 'task'
    | 'orgTask'
    | 'canUpdate'
    | 'isActionPending'
    | 'isLoading'
    | 'windowLevel'
    | 'onUpdateTask'
    | 'onUpdateOrgTask'
    | 'onSave'
    | 'onCancel'
  > {
  //switches to the classic task dialog with the task as entered so far
  onOpenFullDialog: () => void;
  //rendered in step 1, right of the checklist label (for example an AI actions button)
  aiChecklistActions?: ReactNode;
  //rendered under the content of the current step (for example a result bar)
  renderAIResult?: (step: WizardStep) => JSX.Element | null;
}

// Creating a new task in two short steps (what, who and when) instead of the full task dialog.
const NewTaskWizard = (props: INewTaskWizardProps) => {
  const { t } = useTranslation(['translation', 'task']);
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const [step, setStep] = useState<WizardStep>(1);
  const [err, setErr] = useState<Joi.ValidationError | undefined>(undefined);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const stepChangedAtRef = useRef<number>(0);

  useEffect(() => {
    if (!props.isOpen) return;

    setStep(1);

    //the user has to type a name: do not offer the default name as if it were one
    if (props.task.taskId === -1 && props.task.name === t('translation:Task.Name.Default')) {
      const copy = props.task.clone();
      copy.name = '';
      props.onUpdateTask(copy);

      const orgCopy = props.orgTask.clone();
      orgCopy.name = '';
      props.onUpdateOrgTask(orgCopy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  useEffect(() => {
    const result = props.task.validate(getTaskValidationLabels(t));
    setErr(result.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task]);

  //
  // Helpers
  //
  const hasName = (): boolean => {
    return !!props.task.name?.trim();
  };

  const hasChecklistItems = props.task.checkList.items.length > 0;

  //the same condition the classic checklist uses for its form hint
  const canUseForms =
    props.canUpdate && !appContext.isMobileView && hasUserFeature(appContext, FeatureTypes.KPIsAndForms);

  const getErrorMessage = (fieldName: string): string | undefined => {
    //an empty name is not flagged: the buttons stay disabled until the user typed one
    if (fieldName === 'name' && !hasName()) return undefined;

    return findTaskFieldError(err, fieldName);
  };

  const isPrimaryDisabled = (): boolean => {
    if (step === 1) return !hasName() || props.isActionPending || props.isLoading;

    return !hasName() || !!err || props.isActionPending || props.isLoading;
  };

  const goToStep1 = () => {
    stepChangedAtRef.current = Date.now();
    setStep(1);
  };

  const onCancel = () => {
    if (props.task.isEqual(props.orgTask)) {
      props.onCancel();
    } else {
      setShowCancelDialog(true);
    }
  };

  const runPrimary = () => {
    if (isPrimaryDisabled()) return;
    if (Date.now() - stepChangedAtRef.current < stepChangeGuardMs) return;

    if (step < wizardStepCount) {
      stepChangedAtRef.current = Date.now();
      setStep(2);
    } else {
      props.onSave(props.task, false);
    }
  };

  //Ctrl/Cmd+Enter anywhere in the dialog; a plain Enter stays with the field
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      runPrimary();
    }
  };

  const onUpdateCheckList = (newCheckList: TaskCheckList) => {
    const copyTask = props.task.clone();
    copyTask.checkList = newCheckList;
    props.onUpdateTask(copyTask);
  };

  const applyDeadlineDay = (day: Date) => {
    const copy = props.task.clone();
    setWizardDeadlineDay(copy, day);
    props.onUpdateTask(copy);
  };

  //
  // Render helpers
  //
  const getHeader = () => {
    return (
      <IPStack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        tokens={globalStackTokensGapMedium}
        styles={{ root: { ...getWizardSectionStyles(), paddingTop: 12 } }}
      >
        <IPStack.Item styles={{ root: { minWidth: 0 } }}>
          <IPText variant="large" styles={globalTextStylesBold}>
            {step === 1 ? t('task:NewTaskWizard.Title1') : t('task:NewTaskWizard.Title2')}
          </IPText>
        </IPStack.Item>
        <IPStack.Item grow />
        <IPStack.Item>
          <IPIconButton iconProps={cancelIcon} title={t('translation:General.Button.Cancel')} onClick={onCancel} />
        </IPStack.Item>
      </IPStack>
    );
  };

  //the two steps at the outer edges with a line between them; the shared WizardProgress centers
  //each step in an equal column, which leaves wide margins with only two steps
  const getProgress = () => {
    const dotSize = 14 * stepIconSizeRatios[StepIconSizes.Medium];
    //both steps get the same width, so the line between them sits in the middle whatever the label length
    const stepItemStyles = { root: { width: wizardStepLabelWidth, flexShrink: 0 } };

    return (
      <IPStack
        horizontal
        verticalAlign="start"
        styles={{ root: { ...getWizardSectionStyles(), paddingTop: 8, paddingBottom: 12 } }}
      >
        <IPStack.Item styles={stepItemStyles}>
          <StepIcon
            mode={step === 1 ? StepIconModes.Active : StepIconModes.Success}
            index={1}
            size={StepIconSizes.Medium}
            label={t('task:NewTaskWizard.Step1')}
          />
        </IPStack.Item>
        <IPStack.Item grow styles={{ root: { paddingTop: (dotSize - 1) / 2, margin: '0 8px' } }}>
          <div style={{ borderTop: `1px solid ${theme.semanticColors.bodyDivider}` }} />
        </IPStack.Item>
        <IPStack.Item styles={stepItemStyles}>
          <StepIcon
            mode={step === 2 ? StepIconModes.Active : StepIconModes.NotReached}
            index={2}
            size={StepIconSizes.Medium}
            label={t('task:NewTaskWizard.Step2')}
          />
        </IPStack.Item>
      </IPStack>
    );
  };

  //the common deadlines as the shared suggestions panel, one click sets the deadline
  const getDeadlineSuggestions = () => {
    if (!props.canUpdate) return null;

    const today = new Date();
    const options = [
      { id: 'tomorrow', iconName: 'Calendar', label: t('task:NewTaskWizard.Tomorrow'), day: addDateDays(today, 1) },
      { id: 'week', iconName: 'Calendar', label: t('task:NewTaskWizard.InAWeek'), day: addDateDays(today, 7) },
      { id: 'twoWeeks', iconName: 'Calendar', label: t('task:NewTaskWizard.InTwoWeeks'), day: addDateDays(today, 14) },
      { id: 'month', iconName: 'Calendar', label: t('task:NewTaskWizard.EndOfMonth'), day: getEndOfMonth(today) },
    ];

    return (
      <InlineSuggestions
        items={options}
        onSelect={(option) => applyDeadlineDay(option.day)}
        defaultExpanded={true}
        text={{
          show: t('task:NewTaskWizard.DeadlineSuggestions.Show'),
          label: t('task:NewTaskWizard.DeadlineSuggestions.Label'),
          hint: t('task:NewTaskWizard.DeadlineSuggestions.Hint'),
          dismiss: t('task:NewTaskWizard.DeadlineSuggestions.Dismiss'),
        }}
      />
    );
  };

  const getStepContent = () => {
    if (step === 1) {
      return (
        <IPStack verticalFill disableShrink tokens={globalStackTokensGapMedium}>
          <TaskDetailName
            orgTask={props.orgTask}
            task={props.task}
            canUpdate={props.canUpdate}
            onUpdate={props.onUpdateTask}
            getErrorMessage={getErrorMessage}
          />
          <TaskDetailDescription
            orgTask={props.orgTask}
            task={props.task}
            canUpdate={props.canUpdate}
            onUpdate={props.onUpdateTask}
          />
          {/* with items the checklist takes the free height of the step, scrolls in its own pane */}
          <IPStack.Item
            grow={hasChecklistItems}
            styles={{
              root: { minHeight: hasChecklistItems ? wizardChecklistMinHeight : undefined, position: 'relative' },
            }}
          >
            <IPStack verticalFill tokens={globalStackTokensGapExtraSmall}>
              <IPStack.Item>
                <IPStack horizontal verticalAlign="center" horizontalAlign="space-between">
                  <IPStack.Item>
                    <TextInfo
                      message={canUseForms ? t('task:CheckList.UpgradeFormInfo') : undefined}
                      learnMoreLink={globalKB_howToWorkWithFormsChecklist}
                    >
                      <IPLabel>{t('task:CheckList.Label')}</IPLabel>
                    </TextInfo>
                  </IPStack.Item>
                  {props.aiChecklistActions && <IPStack.Item>{props.aiChecklistActions}</IPStack.Item>}
                </IPStack>
              </IPStack.Item>
              <IPStack.Item grow>
                <ChecklistContainer
                  task={props.task}
                  orgTask={props.orgTask}
                  onUpdateCheckList={onUpdateCheckList}
                  mode={ChecklistMode.User}
                  canUpdate={props.canUpdate}
                  hideEmptyState
                />
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
        </IPStack>
      );
    }

    return (
      <IPStack tokens={globalStackTokensGapMedium}>
        <TaskDetailAssignment
          orgTask={props.orgTask}
          task={props.task}
          canUpdate={props.canUpdate}
          onUpdate={props.onUpdateTask}
          getErrorMessage={getErrorMessage}
        />
        <TaskDetailDeadline
          orgTask={props.orgTask}
          task={props.task}
          canUpdate={props.canUpdate}
          onUpdate={props.onUpdateTask}
          getErrorMessage={getErrorMessage}
          simple
        />
        {getDeadlineSuggestions()}
      </IPStack>
    );
  };

  const getFooter = () => {
    const primaryDisabled = isPrimaryDisabled();

    return (
      <IPStack
        horizontal
        verticalAlign="center"
        horizontalAlign="space-between"
        tokens={globalStackTokensGapMedium}
        styles={{ root: { ...getWizardSectionStyles(), paddingBottom: 16 } }}
      >
        <IPStack.Item>
          <IPDefaultButton
            iconProps={extLinkIcon}
            title={t('task:NewTaskWizard.MoreOptionsTitle')}
            disabled={props.isActionPending}
            onClick={props.onOpenFullDialog}
          >
            {t('task:NewTaskWizard.MoreOptions')}
          </IPDefaultButton>
        </IPStack.Item>
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapExtraSmall}>
            {step > 1 && (
              <IPDefaultButton iconProps={leftArrowIcon} onClick={goToStep1}>
                {t('task:NewTaskWizard.Back')}
              </IPDefaultButton>
            )}
            <IPDefaultButton onClick={onCancel}>{t('translation:General.Button.Cancel')}</IPDefaultButton>
            <IPTooltipHost content={step === 1 && !hasName() ? t('task:NewTaskWizard.NameFirst') : undefined}>
              <IPPrimaryButton key={`primary-${step}`} disabled={primaryDisabled} onClick={runPrimary}>
                {step < wizardStepCount ? t('task:NewTaskWizard.Next') : t('task:NewTaskWizard.Create')}
              </IPPrimaryButton>
            </IPTooltipHost>
          </IPStack>
        </IPStack.Item>
      </IPStack>
    );
  };

  //
  // Main render
  //
  return (
    <Fragment>
      {/* the focus trap stays off: the pickers and menus in the steps open callouts in their own
          layer, which a trap steals focus back from */}
      <IPModal
        forceFocusInsideTrap={false}
        isOpen={props.isOpen}
        styles={getTaskViewModalStyles(props.windowLevel)}
        onDismiss={onCancel}
      >
        {props.isLoading && <OverlayLoader text={t('task:Progress.Save')} />}
        <IPStack styles={{ root: getWizardRootStyles() }} onKeyDown={onKeyDown}>
          {getHeader()}
          {getProgress()}
          <IPSeparator />
          <IPStack.Item grow styles={{ root: getWizardBodyStyles() }}>
            <IPStack verticalFill disableShrink tokens={globalStackTokensGapMedium}>
              <IPStack.Item grow>{getStepContent()}</IPStack.Item>
              {props.renderAIResult && props.renderAIResult(step)}
            </IPStack>
          </IPStack.Item>
          <IPSeparator />
          {getFooter()}
        </IPStack>
      </IPModal>
      <DialogYesNo
        onNo={() => setShowCancelDialog(false)}
        onYes={() => {
          setShowCancelDialog(false);
          props.onCancel();
        }}
        title={t('task:Cancel.DialogTitle')}
        subText={t('task:Cancel.DialogSubTitle')}
        hidden={!showCancelDialog}
      />
    </Fragment>
  );
};

export default NewTaskWizard;
